import type { FragmentOf } from '@/utilities/gql';
import { Button } from '@simplywallst/ui-core/next';
import EditSolid from '@simplywallst/ui-core/icons/EditSolid';
import {
  ROUTE_NARRATIVE_UPDATE_CREATE_EDIT,
  ROUTE_PUBLIC_NARRATIVE_EDIT,
} from '@/constants/routes';
import { getPath } from '@/router/utils';
import { isAfter } from 'date-fns';
import { useFlag } from '@simplywallst/unleash';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useTracking } from 'react-tracking';
import type { ComponentProps } from 'react';
import type { NARRATIVE_EDIT_BUTTON_FRAGMENT } from './narrativeEditButtonFragment';

type Props = {
  narrative: FragmentOf<typeof NARRATIVE_EDIT_BUTTON_FRAGMENT>;
  returnPath?: string;
  size?: ComponentProps<typeof Button>['size'];
};

export const NarrativeEditButton = ({
  narrative,
  returnPath,
  size = 'large',
}: Props) => {
  const { publicId: currentUserPublicId } = useUserProfile();
  const {
    id: narrativeId,
    owner,
    companyId,
    latestPublishedUpdate,
    earliestPublishedUpdate,
  } = narrative;
  const { publicId } = owner;
  const isMyNarrative = currentUserPublicId === publicId;
  const { trackEvent } = useTracking();
  const { enabled: isUpdatesViewEnabled } = useFlag(
    'mono-narrative-updates-view'
  );

  const getMyNarrativeEditHref = () => {
    if (!isMyNarrative) return null;

    const isEditableWithinTimelock =
      latestPublishedUpdate?.willLockAt &&
      isAfter(new Date(latestPublishedUpdate.willLockAt), new Date());

    const isNarrativeDraft = !latestPublishedUpdate;
    const isNarrative =
      isNarrativeDraft ||
      (latestPublishedUpdate &&
        earliestPublishedUpdate?.id === latestPublishedUpdate?.id &&
        isEditableWithinTimelock);

    if (!isUpdatesViewEnabled || isNarrative)
      return getPath({
        type: ROUTE_PUBLIC_NARRATIVE_EDIT,
        payload: {
          id: narrativeId,
          query: {
            company: companyId ? companyId.toLowerCase() : undefined,
            returnPath,
          },
        },
      });

    return getPath({
      type: ROUTE_NARRATIVE_UPDATE_CREATE_EDIT,
      payload: {
        companyid: companyId,
        query: {
          returnPath,
        },
      },
    });
  };

  const href = getMyNarrativeEditHref();

  if (!href) return null;

  return (
    <Button
      variant="outline"
      size={size}
      href={href}
      className="text-center"
      onClick={() => {
        trackEvent({
          action: 'click',
          target: 'edit-narrative',
          valuationType: 'user_defined',
        });
      }}
    >
      <EditSolid className="!mr-1" />
      Edit
    </Button>
  );
};
